<script lang="ts" setup>
import type { RouteLocationNormalized } from 'vue-router'
import isArray from 'lodash/isArray'
import type { Article } from '~/types'

definePageMeta({
  layout: false,
  validate: (route: RouteLocationNormalized) => routeValidate(route),
})

const { params } = useRoute()
const currentEntity = useCurrentEntity()

const password = ref<string>()

const entitySlug = computed<string>(() => {
  const slugs = params.slug

  if (isArray(slugs) && slugs.length > 0) {
    const filteredSlugs = slugs.filter(Boolean)

    return filteredSlugs[filteredSlugs.length - 1]
  }

  return slugs?.toString()
})

const requestParams = computed<Article.SearchRequest>(() => ({
  include: [
    'articleCurrentVersion',
    'author',
    'categories',
    'image',
    'teams.logo',
    'teams.menus',
  ],
  password: password.value,
}))

const articleTag = computed<string>(() => `article-${entitySlug.value}`)

const { errorObject } = useShowApiError('Article Not Found')
const { fetchData } = useLoadArticleBySlug(entitySlug.value, requestParams)
const { data, error } = await useAsyncData(
  articleTag,
  () => fetchData(),
  {
    watch: [requestParams],
  },
)

if (error.value) {
  throw errorObject
}

const { data: unwrappedData, key } = usePasswordProtection<Article.Model>(data)
const {
  article,
  headInput,
  id,
  layoutKey,
  seoMetaInput,
  team,
  title,
} = useArticle(unwrappedData)

const setMeta = (): void => {
  currentEntity.value = {
    title: title.value || 'Article',
    dashboardUrl: `resources/articles/${id.value}`,
    type: 'article',
  }

  useSeoMeta(seoMetaInput.value)
  useHead(headInput.value)
  provide('currentTeam', team)
}

setMeta()
watch([data], () => {
  setMeta()
})
</script>

<template>
  <NuxtLayout
    v-if="data"
    :name="layoutKey"
    :team
  >
    <UIPasswordProtected
      :key
      :data
      entity-name="article"
      @submit="password = $event"
    >
      <Article
        v-if="article"
        :article
      />
    </UIPasswordProtected>
  </NuxtLayout>
</template>
